/**
 * Fleet analytics (PostHog) — pure helpers behind the storefront's typed
 * `useTrack()` emits (taxonomy: @owlcat/app-shell analytics-events.ts, "shop"
 * section). Framework-free so they unit-test in node.
 *
 * Rules: NO PII — only ids of our objects, counts, minor-unit money + currency.
 */
import type { HttpTypes } from "@medusajs/types"
import type { FleetEventName, FleetEvents, Track } from "@owlcat/app-shell"

type StorageLike = Pick<Storage, "getItem" | "setItem">

/** Emit `event` at most once per `key` (sessionStorage by default). Returns true when emitted. */
export function trackOnce<E extends FleetEventName>(
  track: Track,
  key: string,
  event: E,
  properties: FleetEvents[E],
  storage: StorageLike | null | undefined = typeof sessionStorage === "undefined" ? null : sessionStorage
): boolean {
  const storageKey = `owlcat.analytics.${key}`
  try {
    if (storage?.getItem(storageKey)) return false
    storage?.setItem(storageKey, "1")
  } catch {
    // private mode / blocked storage → still emit once per mount
  }
  track(event, properties)
  return true
}

/** Currencies Medusa/Stripe count in whole units (no minor subdivision). */
const NO_DIVISION_CURRENCIES = new Set(["krw", "jpy", "vnd", "clp", "pyg", "xaf", "xof", "bif", "djf", "gnf", "kmf", "mga", "rwf", "ugx", "vuv", "xpf"])

/** Medusa v2 totals are MAJOR units (199.5 CZK) → minor units (19950). */
export function toMinor(amount: number | null | undefined, currency: string | null | undefined): number {
  const value = Number(amount) || 0
  const code = (currency ?? "").toLowerCase()
  return Math.round(NO_DIVISION_CURRENCIES.has(code) ? value : value * 100)
}

type MetaItem = { metadata?: Record<string, unknown> | null; quantity?: number | null }

/** Real purchasable lines — service / return-shipping helper lines excluded. */
export function countRealItems(items: MetaItem[] | null | undefined): number {
  return (items ?? []).filter((it) => {
    const m = it.metadata ?? {}
    return m.isServiceItem !== true && m.isServiceItem !== "true" && m.isReturnShipping !== true && m.isReturnShipping !== "true"
  }).length
}

export function paymentMethodOf(providerId: string | null | undefined): FleetEvents["order_completed"]["method"] {
  if (!providerId) return "unknown"
  if (providerId.startsWith("pp_stripe_") || providerId.startsWith("pp_medusa-")) return "stripe"
  if (providerId.startsWith("pp_system_default") || providerId.startsWith("pp_manual")) return "manual"
  return "unknown"
}

export function productViewedProps(product: Pick<HttpTypes.StoreProduct, "id" | "categories" | "collection">): FleetEvents["product_viewed"] {
  const category = product.categories?.[0]?.handle ?? product.collection?.handle ?? undefined
  return category ? { product_id: product.id, category } : { product_id: product.id }
}

/** After add-to-cart: resolve the variant back to its product via the returned cart. */
export function cartAddedProps(
  cart: Pick<HttpTypes.StoreCart, "items"> | null | undefined,
  variantId: string,
  quantity: number
): FleetEvents["cart_added"] {
  const line = cart?.items?.find((it) => it.variant_id === variantId)
  return { product_id: line?.product_id ?? `variant:${variantId}`, quantity }
}

export function orderStartedProps(cart: Pick<HttpTypes.StoreCart, "id" | "items">): FleetEvents["order_started"] {
  return { cart_id: cart.id, items: countRealItems(cart.items) }
}

export function orderCompletedProps(
  order: Pick<HttpTypes.StoreOrder, "id" | "items" | "total" | "currency_code" | "payment_collections">
): FleetEvents["order_completed"] {
  const providerId = order.payment_collections?.[0]?.payments?.[0]?.provider_id
  return {
    order_id: order.id,
    items: countRealItems(order.items),
    amount_minor: toMinor(order.total, order.currency_code),
    currency: (order.currency_code ?? "").toUpperCase(),
    method: paymentMethodOf(providerId),
  }
}
